import { MouseEventHandler, useEffect, useRef, useState } from 'react';

import List from 'antd/es/list';
import { useQuery } from '@tanstack/react-query';

import { HotelWebUrlType, WebUrlType } from '@api/index';
import {
  FireIcon,
  HistoryFill,
  LocationPinIcon,
  DeleteBin5Line,
} from '@assets/icons';
import SearchBarSearchInput from '@components/ui/search/SearchBarInput/SearchBarSearchInput/SearchBarSearchInput';
import { SearchResultListComponentProps } from '@components/ui/search/SearchBarInput/SearchBarSearchInput/SearchBarSearchInputInterface';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import CustomIcon from '@components/utils/CustomIcon/index';
import ResponsiveListingSearchLocation from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchLocation';
import {
  useCustomMobileDesktopBreakpoint,
  useDebounceValue,
} from '@hooks/hooks';
import { LANDING_BREAKPOINT_VALUE } from '@utils/theme/breakPoint';

import {
  BookerSearchInputWrapper,
  ClearSearchButton,
  HotelSeachWrapper,
  ItemErrorText,
  ItemText,
  ListItem,
  ListItemContent,
  ResponsiveListingSearchLocationContainer,
  StyledSearchContainer,
  Text,
  TextContainer,
} from './styled';

enum BookerWebUrlType {
  LOCATION = 'Location',
  HOTEL = 'Hotel',
}

export type BookerSearchViewModel = {
  id: number;
  text?: string;
  type?: BookerWebUrlType | HotelWebUrlType | WebUrlType;
  url?: string;
};

interface IPastSearchesHeader {
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  title?: string;
  iconType?: 'history' | 'popular';
}

const Icons = {
  history: HistoryFill,
  popular: FireIcon,
};

export const PastSearchesHeader: React.FC<IPastSearchesHeader> = ({
  title = 'Son Aramalar',
  onClearClick,
  iconType = 'history',
}) => {
  const Icon = Icons[iconType];

  return (
    <StyledSearchContainer>
      <TextContainer>
        <CustomIcon paletteColor='grey600' fontSize={16} icon={Icon} />
        <Text color='grey600'>{title}</Text>
      </TextContainer>
      <TextContainer>
        {typeof onClearClick === 'function' ? (
          <ClearSearchButton onClick={onClearClick}>
            <CustomIcon
              paletteColor='grey600'
              fontSize={16}
              icon={DeleteBin5Line}
            />
          </ClearSearchButton>
        ) : null}
      </TextContainer>
    </StyledSearchContainer>
  );
};

export const PopulerSearchHeader: React.FC = () => (
  <StyledSearchContainer>
    <TextContainer>
      <span>
        <CustomIcon paletteColor='grey600' fontSize={24} icon={FireIcon} />
      </span>
      <Text color='grey600'>Populer Aramalar</Text>
    </TextContainer>
  </StyledSearchContainer>
);

export const SearchResult: React.FC<
  SearchResultListComponentProps & {
    data: BookerSearchViewModel[];
    history?: BookerSearchViewModel[];
    showPastSearch?: boolean;
    popularData?: BookerSearchViewModel[];
    onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
    callBack?: (item: BookerSearchViewModel) => void;
    onRemoveItem?: (item: BookerSearchViewModel) => void;
    inputLocation?: string;
  }
> = ({
  searchText,
  onChange,
  data = [],
  history = [],
  showPastSearch,
  onClearClick,
  callBack,
  onRemoveItem,
  inputLocation = '',
}) => {
  const showPastSearches =
    history.length > 0 && showPastSearch && searchText.length === 0;

  return (
    <BookerSearchInputWrapper>
      {showPastSearches ? (
        <>
          <PastSearchesHeader onClearClick={onClearClick} />

          <StyledList
            searchText={searchText}
            data={history.slice(0, 3)}
            onChange={onChange}
            callBack={callBack}
            showRemoveButton={true}
            onRemoveItem={onRemoveItem}
            inputLocation={inputLocation}
          />
        </>
      ) : null}
      {/* TODO: popular search items was move to V2  */}
      {/* {popularData.length > 0 ? (
      <>
        <PopulerSearchHeader />
        <StyledList
          searchText={searchText}
          data={popularData}
          onChange={onChange}
        />
      </>
    ) : null} */}

      {data.length > 0 ? (
        <>
          <StyledList
            searchText={searchText}
            data={data || []}
            onChange={onChange}
            inputLocation={inputLocation}
          />
        </>
      ) : null}
    </BookerSearchInputWrapper>
  );
};

const handleStrongLetter = (input: string, text: string) => {
  const inputLength = input.length;
  let boldText = '';
  let part1 = '';
  let part2 = '';
  let isBoldText = false;
  for (let i = 0; i <= text.length - input.length; i++) {
    if (
      text.slice(i, i + inputLength).toLocaleUpperCase('tr-TR') ===
      input.toLocaleUpperCase('tr-TR')
    ) {
      part1 = text.slice(0, i);
      boldText = text.slice(i, i + inputLength);
      part2 = text.slice(i + inputLength, text.length);
      isBoldText = true;
      break;
    }
  }
  if (isBoldText != false) {
    return (
      <span>
        {part1}
        <strong>{boldText}</strong>
        {part2}
      </span>
    );
  } else {
    return <span>{text}</span>;
  }
};

export const BookerResultList: React.FC<{
  item: BookerSearchViewModel;
  onChange: (item: BookerSearchViewModel) => void;
  callBack?: (item: BookerSearchViewModel) => void;
  showRemoveButton?: boolean;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  inputLocation?: string;
}> = ({
  item,
  onChange,
  callBack,
  onRemoveItem,
  showRemoveButton = false,
  inputLocation = '',
}) => {
  const handleItemClick = () => {
    onChange(item);
    callBack && callBack(item);
  };

  return (
    <>
      {item?.text ? (
        <ListItem>
          <ListItemContent onClick={handleItemClick}>
            <CustomIcon
              paletteColor='grey600'
              fontSize={24}
              icon={getLocationIcon(item?.type || '')}
            />
            <ItemText>
              {inputLocation
                ? handleStrongLetter(inputLocation, item.text)
                : item.text}
            </ItemText>
          </ListItemContent>
          {showRemoveButton ? (
            <ClearSearchButton
              onClick={onRemoveItem ? () => onRemoveItem(item) : undefined}
            >
              <CustomIcon
                paletteColor='grey600'
                fontSize={24}
                icon={DeleteBin5Line}
              />
            </ClearSearchButton>
          ) : null}
        </ListItem>
      ) : null}
    </>
  );
};

export const StyledList: React.FC<{
  data: BookerSearchViewModel[];
  searchText: string;
  onChange: (item: BookerSearchViewModel) => void;
  callBack?: (item: BookerSearchViewModel) => void;
  showRemoveButton?: boolean;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  inputLocation?: string;
}> = ({
  data,
  onChange,
  callBack,
  showRemoveButton,
  onRemoveItem,
  inputLocation = '',
}) => (
  <List
    bordered
    dataSource={data}
    renderItem={(item: BookerSearchViewModel) => (
      <BookerResultList
        item={item}
        onChange={() => onChange(item)}
        callBack={callBack}
        showRemoveButton={showRemoveButton}
        onRemoveItem={onRemoveItem}
        inputLocation={inputLocation}
      />
    )}
  />
);

export const BookerSearchDrawer: React.FC<{
  type: string;
  placeholder: string;
  onInputLocationChange?: (item: BookerSearchViewModel) => void;
  location?: BookerSearchViewModel | undefined;
  responsiveTitle: string;
  fetchItems: (param: string) => Promise<BookerSearchViewModel[]>;
  onHrefUpdate?: (href: string) => void;
  onSearchItemTextUpdate?: (itemText: string) => void;
  history?: BookerSearchViewModel[];
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  callBack?: (item: BookerSearchViewModel) => void;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  error?: boolean;
  href?: string;
}> = ({
  placeholder,
  type,
  location: value,
  onInputLocationChange,
  responsiveTitle,
  fetchItems,
  onHrefUpdate,
  onSearchItemTextUpdate,
  history,
  onClearClick,
  callBack,
  onRemoveItem,
  error,
  href,
}) => {
  const { xl } = useCustomMobileDesktopBreakpoint(LANDING_BREAKPOINT_VALUE);
  const [isShowSearchListingLocation, setIsSHowSearchListingLocation] =
    useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openAutoCompleteResult, setOpenAutoCompleteResult] =
    useState<boolean>(false);
  const [locationInputError, setLocationInputError] = useState(false);
  const [showPastSearch, setShowPastSearch] = useState(false);
  const [location, setLocation] = useState<BookerSearchViewModel>();
  const [inputLocation, setInputLocation] = useState<string>(
    location?.text || ''
  );
  const debouncedLocation = useDebounceValue<string>(inputLocation);
  const [isDataFetched, setIsDataFetched] = useState(false);

  const {
    data: locations,
    isLoading,
    isFetched,
  } = useQuery(
    ['locationItems', debouncedLocation],
    () => fetchItems(debouncedLocation.toLowerCase()),
    { enabled: debouncedLocation.length >= 3 }
  );
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputLocation(e.target.value);
    setIsDataFetched(false);
  };

  useEffect(() => {
    if (value) {
      setInputLocation(value.text || '');
      setLocation(value);
    }
  }, [value]);

  useEffect(() => {
    if (isFetched) {
      setIsDataFetched(isFetched);
    }
  }, [isFetched]);

  const handleLocationClicked = (item: BookerSearchViewModel): void => {
    onSearchItemTextUpdate?.(item.text || '');
    onHrefUpdate?.(item.url || '');
    setInputLocation(item.text || '');
    onInputLocationChange?.(item);
    if (type === 'Tour') {
      const currentHref = href?.split('?')?.[1];
      onHrefUpdate?.(`${item.url}${currentHref ? `?${currentHref}` : ''}`);
      return;
    }
  };

  const applySearchLocationHandler = (
    currentSelectedLocation: BookerSearchViewModel
  ) => {
    onSearchItemTextUpdate?.(currentSelectedLocation?.text || '');
    onHrefUpdate?.(currentSelectedLocation?.url || '');
    setInputLocation(currentSelectedLocation?.text || '');
    onInputLocationChange?.(currentSelectedLocation);
    setLocation(currentSelectedLocation);
    setIsSHowSearchListingLocation(false);
    if (!currentSelectedLocation?.text) return setLocationInputError(true);
  };

  const handleCloseLocationSearch = (): void => {
    setIsSHowSearchListingLocation(false);
    setLocation(location);
    onHrefUpdate?.('');
    setOpenAutoCompleteResult(false);
  };

  return xl ? (
    <HotelSeachWrapper>
      <SearchBarSearchInput
        label='Nereye Gideceksiniz?'
        inputProps={{
          value: inputLocation,
          placeholder,
          onChange: handleChange,
          onFocus: () => {
            setShowPastSearch(true);
          },
          inputMode: 'search',
        }}
        leftIcon={() => (
          <CustomIcon
            fontSize={28}
            paletteColor='primaryBrand'
            icon={LocationPinIcon}
          />
        )}
        popoverProps={{
          align: 'end',
        }}
        error={error}
        SearchResultListComponent={({ onChange }) => {
          const validLocations =
            locations && locations.length > 0 ? true : false;
          const validHistory = history && history?.length > 0;

          return (validHistory && inputLocation.length === 0) ||
            (inputLocation.length > 2 && validLocations) ? (
            <SearchResult
              onChange={(item: BookerSearchViewModel) => {
                onChange(item.text);
                handleLocationClicked(item);
              }}
              searchText={inputLocation || ''}
              inputLocation={inputLocation}
              data={locations || []}
              history={history}
              showPastSearch={showPastSearch}
              onClearClick={onClearClick}
              callBack={callBack}
              onRemoveItem={onRemoveItem}
            />
          ) : isDataFetched && inputLocation.length > 2 && !isLoading ? (
            <ItemErrorText>
              Aradığınız kriterlere uygun sonuç bulunamadı.
            </ItemErrorText>
          ) : null;
        }}
      />
    </HotelSeachWrapper>
  ) : (
    <ResponsiveListingSearchLocationContainer>
      <ResponsiveListingSearchLocation
        type={type}
        showLocationModal={() => setIsSHowSearchListingLocation(true)}
        width='100%'
        height='100%'
        placement='top'
        title={responsiveTitle}
        open={isShowSearchListingLocation}
        locationInput={inputLocation || ''}
        locationInputError={locationInputError}
        handleUpdateButton={applySearchLocationHandler}
        inputRef={inputRef}
        handleCloseLocationSearch={handleCloseLocationSearch}
        openAutoCompleteResultModal={openAutoCompleteResult}
        setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
        pastSearchesData={history}
        afterOpenChange={(open) => {
          if (open) {
            inputRef?.current?.focus();
          }
        }}
        onClearClick={onClearClick}
        error={error || false}
        callBack={callBack}
        onRemoveItem={onRemoveItem}
      />
    </ResponsiveListingSearchLocationContainer>
  );
};
